import { Transaction, PrintSettings, PosConfig, KotTicket } from '../types';
import { getPrintSettingsSync, getAppSettingsSync } from './db';
import { isOn } from './appSettings';
import { shouldPrintUpiQr, upiConfigured, upiUri, qrSvgString } from './upi';
import { formatDate, formatTime } from './datetime';
import { usbPrinterConfigured, printBytes } from './usbPrinter';
import { btPrinterConfigured, printBtBytes } from './btPrinter';
import { ethernetSelected, netReceiptConfigured, printNetReceipt } from './netPrinter';
import { buildReceiptEscPos, buildKotEscPos, buildCouponEscPos, buildReportEscPos } from './escpos';

// Day/Night shift marker ("10 — Day Setting / C - Enable Day/Night"):
// 6 AM – 6 PM counts as DAY, the rest as NIGHT.
export const dayNightOf = (iso: string): 'DAY' | 'NIGHT' => {
  const h = new Date(iso).getHours();
  return h >= 6 && h < 18 ? 'DAY' : 'NIGHT';
};

// Builds + prints an HTML receipt that honors the saved Print Settings
// (print_setting_fragment_layout.xml: header fields, logo, decimal precision,
// NET-TOTAL font size, footers, paper size, copies).

const widthForPaper = (size: PrintSettings['paperSize']): string =>
  size === '2 Inch' ? '58mm' : size === '3 Inch' ? '80mm' : '210mm';

const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export function buildReceiptHtml(
  tx: Transaction,
  config: PosConfig,
  settings: PrintSettings = getPrintSettingsSync()
): string {
  const s = settings;
  const app = getAppSettingsSync();
  const on = (k: string) => isOn(app, k);
  // Clamp to a valid toFixed range (0–2) so a malformed setting can never throw.
  const parsed = parseInt(s.decimalDigits, 10);
  const digits = Number.isFinite(parsed) ? Math.min(2, Math.max(0, parsed)) : 2;
  const cur = config.currencySymbol;
  const n = (v: number) => v.toFixed(digits);
  const money = (v: number) => `${cur} ${v.toFixed(digits)}`;

  // Header values fall back to the store config when a field is blank.
  const title = s.title || config.storeName;
  const address = s.address || config.address || '';
  const gst = s.gstNo || config.gstin || '';
  const contact = s.contactNo || config.phone || '';

  const dateStr = formatDate(tx.timestamp);
  const timeStr = formatTime(tx.timestamp);

  const totalQty = tx.items.reduce((a, i) => a + i.quantity, 0);
  const isSplit = !!(tx.payments && tx.payments.length > 1);
  const payMode = isSplit ? 'SPLIT' : (tx.paymentMethod || 'cash').toUpperCase();

  const netClass = s.netTotalFont41 ? 'net big' : s.netTotalFont21 ? 'net medium' : 'net';

  const headerLogo =
    s.printHeaderLogo && s.headerLogo ? `<img class="logo" src="${s.headerLogo}" alt="" />` : '';
  const footerLogo =
    s.printFooterLogo && s.footerLogo ? `<img class="logo" src="${s.footerLogo}" alt="" />` : '';

  const line = (label: string, value: string) =>
    value ? `<div class="ctr">${esc(label)}${esc(value)}</div>` : '';

  const itemRows = tx.items
    .map((i) => {
      const meta = [
        i.attributeText ? esc(i.attributeText) : '',
        i.weight != null ? `WT ${n(i.weight)}${i.unitName ? ' ' + esc(i.unitName) : ''}` : '',
        i.bags != null ? `BAGS ${n(i.bags)}` : '',
      ]
        .filter(Boolean)
        .join(' · ');
      return `<tr>
        <td class="c-name">${esc(i.name)}${meta ? `<div class="c-sub">${meta}</div>` : ''}</td>
        <td class="c-num">${n(i.quantity)}</td>
        <td class="c-num">${n(i.unitPrice)}</td>
        <td class="c-num">${n(i.total)}</td>
      </tr>`;
    })
    .join('');

  const footers = s.doNotPrintFooters
    ? ''
    : [s.footer1, s.footer2, s.footer3, s.footer4, s.footer5]
        .filter(Boolean)
        .map((f) => `<div class="ctr">${esc(f)}</div>`)
        .join('');

  const optionalRow = (label: string, val: number) =>
    val ? `<div class="row"><span>${label}</span><span>${money(val)}</span></div>` : '';

  const receipt = `
    <div class="receipt${s.boldFont ? ' bold' : ''}">
      ${headerLogo}
      <div class="ctr title">${esc(title)}</div>
      ${s.subTitle ? `<div class="ctr">${esc(s.subTitle)}</div>` : ''}
      ${address ? `<div class="ctr">${esc(address)}</div>` : ''}
      ${line('FSSAI : ', s.fssai)}
      ${line('GSTIN : ', gst)}
      ${line('CONTACT NO. : ', contact)}
      ${line('E-Mail : ', s.email)}
      <div class="ctr">${esc(tx.estimate ? 'ESTIMATE' : tx.gstFlag === false ? 'BILL OF SUPPLY' : s.taxInvoice || 'TAX INVOICE')}</div>
      ${on('customer_details_show_on_header') && tx.customerName ? `<div class="ctr title">CUSTOMER : ${esc(tx.customerName)}</div>` : ''}
      ${tx.orderType === 'parcel' && s.parcelName ? `<div class="ctr">${esc(s.parcelName)}</div>` : ''}
      ${tx.orderType === 'self' ? `<div class="ctr">SELF SERVICE</div>` : ''}
      ${tx.table ? `<div class="ctr">TABLE : ${esc(tx.table)}</div>` : ''}
      ${tx.token ? (on('token_number_print') ? `<div class="ctr title">TOKEN NO : ${esc(tx.token)}</div>` : `<div class="ctr">TOKEN : ${esc(tx.token)}</div>`) : ''}
      ${tx.counter ? `<div class="ctr">COUNTER : ${esc(tx.counter)}</div>` : ''}
      ${tx.premise ? `<div class="ctr">PREMISE : ${esc(tx.premise)}</div>` : ''}
      ${on('enable_day_night') ? `<div class="ctr">SHIFT : ${dayNightOf(tx.timestamp)}</div>` : ''}
      ${on('coupon_kot_header_on_bill') && tx.kotRemark ? `<div class="ctr">KOT : ${esc(tx.kotRemark)}</div>` : ''}
      ${on('coupon_kot_header_on_bill') && tx.couponCode ? `<div class="ctr">COUPON : ${esc(tx.couponCode)}</div>` : ''}

      <div class="row">
        <span>${s.serialNo ? `SERIAL NO ${esc(s.serialNo)}` : ''}</span>
        <span>${dateStr}</span>
      </div>
      <div class="row">
        <span>BILL NO ${esc(tx.billNo)}</span>
        <span>${timeStr}</span>
      </div>
      ${tx.invoiceNumber ? `<div class="row"><span>INVOICE NO ${esc(tx.invoiceNumber)}</span><span></span></div>` : ''}
      ${tx.deliveryChallanNo ? `<div class="row"><span>CHALLAN NO ${esc(tx.deliveryChallanNo)}</span><span></span></div>` : ''}
      ${tx.vehicleNo ? `<div class="row"><span>VEHICLE ${esc(tx.vehicleNo)}</span><span></span></div>` : ''}
      ${tx.customerName ? `<div class="row"><span>Customer: ${esc(tx.customerName)}</span><span></span></div>` : ''}
      ${tx.userName ? `<div class="row"><span>Cashier: ${esc(tx.userName)}</span><span></span></div>` : ''}

      <div class="hr"></div>
      <table>
        <thead>
          <tr><td class="c-name">ITEM NAME</td><td class="c-num">QTY</td><td class="c-num">RATE</td><td class="c-num">AMT</td></tr>
        </thead>
        <tbody>${itemRows}</tbody>
      </table>
      <div class="hr"></div>

      <div class="row"><span>TOTAL QUANTITY</span><span>${n(totalQty)}</span></div>
      <div class="row"><span>AMOUNT</span><span>${money(tx.subtotal)}</span></div>
      ${optionalRow('Discount', tx.discount)}
      ${optionalRow('Service Charge', tx.serviceCharge || 0)}
      ${optionalRow('Tax', tx.tax)}
      ${optionalRow('CESS', tx.cess || 0)}
      ${optionalRow('Delivery', tx.parcelCharges || 0)}
      ${optionalRow('Extra Charges', tx.extraCharges || 0)}
      ${tx.loyaltyDiscount ? `<div class="row"><span>Loyalty</span><span>-${money(tx.loyaltyDiscount)}</span></div>` : ''}
      ${tx.roundOff ? `<div class="row"><span>Round Off</span><span>${money(tx.roundOff)}</span></div>` : ''}
      <div class="row ${netClass}"><span>TOTAL AMOUNT</span><span>${money(tx.total)}</span></div>
      ${
        isSplit
          ? tx.payments!.map((p) => `<div class="row"><span>${esc(p.method.toUpperCase())}</span><span>${money(p.amount)}</span></div>`).join('')
          : tx.amountPaid != null ? `<div class="row"><span>Paid</span><span>${money(tx.amountPaid)}</span></div>` : ''
      }
      ${tx.change ? `<div class="row"><span>Change</span><span>${money(tx.change)}</span></div>` : ''}
      ${tx.loyaltyRedeemed ? `<div class="row"><span>Points Redeemed</span><span>${n(tx.loyaltyRedeemed)}</span></div>` : ''}
      ${tx.loyaltyEarned ? `<div class="row"><span>Points Earned</span><span>${n(tx.loyaltyEarned)}</span></div>` : ''}
      <div class="hr"></div>

      <div class="ctr">PAYMENT MODE : ${esc(payMode)}</div>
      ${
        shouldPrintUpiQr(tx, config)
          ? `<div class="hr"></div><div class="ctr">SCAN TO PAY (UPI)</div><div class="ctr">${qrSvgString(
              upiUri(config, tx.total, `Bill ${tx.billNo}`)
            )}</div><div class="ctr">${esc(config.upiId ?? '')}</div>`
          : ''
      }
      ${s.extraCharge ? `<div class="hr"></div><div class="ctr">${esc(s.extraCharge)}</div>` : ''}
      <div class="hr"></div>
      <div class="ctr">POWERED BY asnasoftware CONNECT</div>
      <div class="hr"></div>
      ${footers}
      ${footerLogo}
    </div>`;

  const width = widthForPaper(s.paperSize);
  const copies = Math.max(1, s.copies || 1);
  const body = Array.from({ length: copies }, () => receipt).join('<div class="pagebreak"></div>');

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Receipt ${esc(tx.billNo)}</title>
<style>
  @page { size: ${width} auto; margin: 0; }
  * { box-sizing: border-box; }
  body { margin: 0; background: #fff; }
  .receipt {
    width: ${width};
    padding: 6px 8px;
    font-family: 'Courier New', monospace;
    font-size: 12px;
    color: #000;
    line-height: 1.45;
  }
  .receipt.bold { font-weight: 700; }
  .title { font-size: 15px; font-weight: 700; }
  .ctr { text-align: center; }
  .row { display: flex; justify-content: space-between; }
  .hr { border-top: 1px dashed #000; margin: 4px 0; }
  table { width: 100%; border-collapse: collapse; }
  td { font-size: 12px; padding: 1px 0; vertical-align: top; }
  .c-name { text-align: left; width: 46%; }
  .c-sub { font-size: 10px; color: #444; }
  .c-num { text-align: right; }
  .net { font-weight: 700; }
  .net.medium span { font-size: 16px; }
  .net.big span { font-size: 22px; }
  .logo { display: block; max-width: 70%; max-height: 80px; object-fit: contain; margin: 4px auto; }
  .pagebreak { page-break-after: always; }
</style>
</head>
<body onload="window.print();">${body}</body>
</html>`;
}

// Opens a hidden print window and prints the receipt for this transaction.
export function printTransaction(
  tx: Transaction,
  config: PosConfig,
  settings: PrintSettings = getPrintSettingsSync()
): void {
  const html = buildReceiptHtml(tx, config, settings);
  const w = window.open('', '_blank', 'width=400,height=600');
  if (!w) {
    alert('Unable to open the print window. Please allow pop-ups for this site.');
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}

// Send ESC/POS bytes to connected thermal printer(s). When the printer type is
// "Ethernet", the network printer is the exclusive transport (no USB/Bluetooth
// fallback). Otherwise a USB and a Bluetooth printer can both be set up at once
// and we print to BOTH so the slip comes out on whichever (or both) are
// configured. Each transport is attempted independently; a failure on one does
// not block the other. Returns true if the bytes were handed to at least one
// hardware printer; false when none is configured, so callers can fall back to
// browser printing. Throws only when every configured printer was tried and all
// of them failed.
async function printEscPos(data: Uint8Array): Promise<boolean> {
  let printed = false;
  let lastErr: unknown = null;

  // Ethernet is an exclusive transport: when selected, receipts go only over the
  // network. A configured-but-failed attempt throws (caller falls back to the
  // browser print window); a missing billing IP returns false (same fallback).
  if (ethernetSelected()) {
    if (!netReceiptConfigured()) return false;
    await printNetReceipt(data);
    return true;
  }

  if (usbPrinterConfigured()) {
    try {
      await printBytes(data);
      printed = true;
    } catch (e) {
      lastErr = e;
      console.warn('USB print failed:', e);
    }
  }

  if (btPrinterConfigured()) {
    try {
      await printBtBytes(data);
      printed = true;
    } catch (e) {
      lastErr = e;
      console.warn('Bluetooth print failed:', e);
    }
  }

  if (printed) return true;

  // A printer was configured but every attempt failed — surface the error so the
  // caller can fall back to the browser print window.
  if (lastErr) throw lastErr;
  return false;
}

// Print a receipt, preferring a configured thermal printer (USB, then
// Bluetooth) and falling back to the browser print window if none is set up or
// the transfer fails.
export async function printReceiptAuto(
  tx: Transaction,
  config: PosConfig,
  settings: PrintSettings = getPrintSettingsSync()
): Promise<void> {
  try {
    if (await printEscPos(buildReceiptEscPos(tx, config, settings))) return;
  } catch (e) {
    console.warn('Thermal receipt print failed, falling back to browser print:', e);
  }
  printTransaction(tx, config, settings);
}

// Print a generic tabular report (Reports / GST / Customer / Transport / stock
// & cashflow slips) to any configured thermal printer — USB, Bluetooth, or
// Ethernet — via the shared ESC/POS report builder. Falls back to the browser
// print dialog when no thermal printer is set up or every attempt fails. The
// browser-print fallback relies on the page's own @media print layout.
export async function printReportAuto(
  title: string,
  subtitle: string,
  columns: string[],
  rows: (string | number)[][]
): Promise<void> {
  try {
    if (await printEscPos(buildReportEscPos(title, subtitle, columns, rows))) return;
  } catch (e) {
    console.warn('Thermal report print failed, falling back to browser print:', e);
  }
  window.print();
}

// Print a KOT to the configured thermal printer (USB or Bluetooth). Returns
// true if it was sent; false when no thermal printer is configured (so the
// caller can decide on a fallback).
export async function printKotUsb(kot: KotTicket, storeName: string): Promise<boolean> {
  return printEscPos(buildKotEscPos(kot, storeName));
}

// ---- Coupon slip (8 — Coupon Setting) -------------------------------------
// A counter/food-court coupon: token + items, handed to the customer to claim
// the order. Format honors the coupon app settings (UPI QR, qty&price format,
// bill-no/serial, double print).

export function buildCouponHtml(
  tx: Transaction,
  config: PosConfig,
  settings: PrintSettings = getPrintSettingsSync()
): string {
  const app = getAppSettingsSync();
  const on = (k: string) => isOn(app, k);
  const cur = config.currencySymbol;
  const width = widthForPaper(settings.paperSize);
  const showPrice = on('new_coupon_format_qty_price');

  const rows = tx.items
    .map(
      (i, idx) => `<div class="row">
        <span>${on('kot_billno_sno') ? `${idx + 1}) ` : ''}${esc(i.name)}${
          i.attributeText ? ` (${esc(i.attributeText)})` : ''
        }</span>
        <span>${i.quantity}${showPrice ? ` × ${cur}${i.unitPrice.toFixed(2)}` : ''}</span>
      </div>`
    )
    .join('');

  const slip = `
    <div class="receipt">
      <div class="ctr title">${esc(settings.title || config.storeName)}</div>
      <div class="ctr title">COUPON</div>
      ${tx.token ? `<div class="ctr big">TOKEN : ${esc(tx.token)}</div>` : ''}
      ${on('kot_billno_sno') ? `<div class="ctr">BILL NO ${esc(tx.billNo)}</div>` : ''}
      <div class="ctr">${formatDate(tx.timestamp)} ${formatTime(tx.timestamp)}</div>
      <div class="hr"></div>
      ${rows}
      <div class="hr"></div>
      ${showPrice ? `<div class="row"><span>TOTAL</span><span>${cur} ${tx.total.toFixed(2)}</span></div>` : ''}
      ${
        on('coupon_upi') && upiConfigured(config)
          ? `<div class="ctr">SCAN TO PAY (UPI)</div><div class="ctr">${qrSvgString(
              upiUri(config, tx.total, `Bill ${tx.billNo}`)
            )}</div>`
          : ''
      }
    </div>`;

  // "E. Double Coupon Print" — two copies of the slip.
  const copies = on('double_coupon_print') ? 2 : 1;
  const body = Array.from({ length: copies }, () => slip).join('<div class="pagebreak"></div>');

  return `<!doctype html>
<html><head><meta charset="utf-8" /><title>Coupon ${esc(tx.billNo)}</title>
<style>
  @page { size: ${width} auto; margin: 0; }
  body { margin: 0; background: #fff; }
  .receipt { width: ${width}; padding: 6px 8px; font-family: 'Courier New', monospace; font-size: 12px; color: #000; line-height: 1.5; }
  .title { font-size: 15px; font-weight: 700; }
  .big { font-size: 22px; font-weight: 800; }
  .ctr { text-align: center; }
  .row { display: flex; justify-content: space-between; }
  .hr { border-top: 1px dashed #000; margin: 4px 0; }
  .pagebreak { page-break-after: always; }
</style></head>
<body onload="window.print();">${body}</body></html>`;
}

// Browser print-window fallback for the coupon slip.
function printCouponWindow(tx: Transaction, config: PosConfig): void {
  const w = window.open('', '_blank', 'width=320,height=480');
  if (!w) return;
  w.document.open();
  w.document.write(buildCouponHtml(tx, config));
  w.document.close();
}

// Print the coupon slip, preferring the configured thermal printer (USB, then
// Bluetooth) and falling back to the browser print window.
export function printCouponSlip(tx: Transaction, config: PosConfig): void {
  printEscPos(buildCouponEscPos(tx, config))
    .then((sent) => {
      if (!sent) printCouponWindow(tx, config);
    })
    .catch((e) => {
      console.warn('Thermal coupon print failed, falling back to browser print:', e);
      printCouponWindow(tx, config);
    });
}

// Coupon auto-print hooks: "A - Coupon Print & Bill Save" fires the slip when a
// bill is saved without printing; "B - Coupon Print & Bill Print" fires it
// alongside the printed bill.
export function maybePrintCoupon(
  tx: Transaction,
  config: PosConfig,
  trigger: 'save' | 'print'
): void {
  const app = getAppSettingsSync();
  const key = trigger === 'save' ? 'coupon_save_bill' : 'coupon_print_bill';
  if (isOn(app, key)) printCouponSlip(tx, config);
}

// ---- A4 delivery note ("16. A4 Delivery Note") -----------------------------
// A full-page goods/delivery note (no prices) for the challan/vehicle bills.

export function buildDeliveryNoteHtml(tx: Transaction, config: PosConfig): string {
  const rows = tx.items
    .map(
      (i, idx) => `<tr><td>${idx + 1}</td><td>${esc(i.name)}${
        i.attributeText ? `<div class="c-sub">${esc(i.attributeText)}</div>` : ''
      }</td><td class="num">${i.quantity}${
        i.unitName ? ' ' + esc(i.unitName) : ''
      }</td></tr>`
    )
    .join('');
  return `<!doctype html>
<html><head><meta charset="utf-8" /><title>Delivery Note ${esc(tx.billNo)}</title>
<style>
  @page { size: A4; margin: 18mm; }
  body { font-family: Arial, sans-serif; color: #000; }
  h1 { text-align: center; margin: 0; }
  h2 { text-align: center; margin: 4px 0 18px; font-weight: 400; font-size: 15px; }
  table { width: 100%; border-collapse: collapse; margin-top: 12px; }
  th, td { border: 1px solid #333; padding: 6px 8px; text-align: left; font-size: 14px; }
  .num { text-align: right; }
  .c-sub { font-size: 11px; color: #444; }
  .meta { display: flex; justify-content: space-between; margin-top: 12px; font-size: 14px; }
  .sign { display: flex; justify-content: space-between; margin-top: 70px; font-size: 13px; }
</style></head>
<body onload="window.print();">
  <h1>${esc(config.storeName)}</h1>
  <h2>DELIVERY NOTE${config.address ? ' — ' + esc(config.address) : ''}</h2>
  <div class="meta">
    <span>Bill No: <b>${esc(tx.billNo)}</b></span>
    <span>Date: ${formatDate(tx.timestamp)}</span>
  </div>
  <div class="meta">
    <span>Challan No: ${esc(tx.deliveryChallanNo || '—')}</span>
    <span>Vehicle No: ${esc(tx.vehicleNo || '—')}</span>
  </div>
  ${tx.customerName ? `<div class="meta"><span>Customer: ${esc(tx.customerName)}</span></div>` : ''}
  <table>
    <thead><tr><th>#</th><th>Item</th><th class="num">Quantity</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <div class="sign"><span>Received by ____________________</span><span>Authorised signatory ____________________</span></div>
</body></html>`;
}

export function printDeliveryNote(tx: Transaction, config: PosConfig): void {
  const w = window.open('', '_blank', 'width=720,height=900');
  if (!w) return;
  w.document.open();
  w.document.write(buildDeliveryNoteHtml(tx, config));
  w.document.close();
}

// ---- Bill via e-mail ("12. Bill Send To Email") ----------------------------
// Plain-text bill in a mailto: link, openable in the default mail client.

export function mailtoBillUrl(tx: Transaction, config: PosConfig): string {
  const cur = config.currencySymbol;
  const lines = [
    config.storeName,
    `Bill No ${tx.billNo} — ${formatDate(tx.timestamp)} ${formatTime(tx.timestamp)}`,
    tx.customerName ? `Customer: ${tx.customerName}` : '',
    '------------------------------',
    ...tx.items.map(
      (i) =>
        `${i.name}${i.attributeText ? ` (${i.attributeText})` : ''}  x${i.quantity}  ${cur}${i.total.toFixed(2)}`
    ),
    '------------------------------',
    `Subtotal: ${cur}${tx.subtotal.toFixed(2)}`,
    tx.discount ? `Discount: ${cur}${tx.discount.toFixed(2)}` : '',
    tx.tax ? `Tax: ${cur}${tx.tax.toFixed(2)}` : '',
    `TOTAL: ${cur}${tx.total.toFixed(2)}`,
    `Payment: ${(tx.paymentMethod || 'cash').toUpperCase()}`,
  ].filter(Boolean);
  const subject = encodeURIComponent(`Bill ${tx.billNo} — ${config.storeName}`);
  const body = encodeURIComponent(lines.join('\n'));
  return `mailto:?subject=${subject}&body=${body}`;
}
